import React from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import '../../../styles/StreamActionBar.css';
import '../../../styles/StreamCta.css';

export const STREAM_ACTIONS = ['request', 'boost', 'tip'];

/**
 * The Request / Boost / Tip buttons above chat (mockup 01, ENG-667). Each
 * opens its panel through `onAction(kind)`; the page decides what that panel
 * is and whether the viewer must sign in first. `disabled` lists actions not
 * available right now (e.g. tips for a band without a payment provider).
 *
 * `disabledReasons` says why (ENG-772): `{ [kind]: reason }`, where reason is
 * a key under `stream.actions.disabledReason.<kind>` (offline, ownBand,
 * noPayments, and noBoostable for Boost). A disabled action with a reason
 * stays rendered and gets a visually hidden hint `#stream-action-<kind>-hint`
 * that its button's `aria-describedby` points at, plus the same text as its
 * `title` tooltip. An enabled action has neither.
 */
const StreamActionBar = ({ onAction, disabled = [], disabledReasons = {}, active = null }) => {
  const { t } = useLocale();
  return (
    <div className="stream-action-bar" role="group" aria-label={t('stream.actions.label')}>
      {STREAM_ACTIONS.map((kind) => {
        const isDisabled = disabled.includes(kind);
        const reason = isDisabled ? disabledReasons[kind] : null;
        const hint = reason ? t(`stream.actions.disabledReason.${kind}.${reason}`) : null;
        const hintId = `stream-action-${kind}-hint`;
        return (
          <React.Fragment key={kind}>
            <button
              type="button"
              className={`stream-action-button stream-action-button--${kind} stream-cta`}
              data-testid={`stream-action-${kind}`}
              onClick={() => onAction?.(kind)}
              disabled={isDisabled}
              aria-pressed={active === kind}
              title={hint ?? undefined}
              aria-describedby={hint ? hintId : undefined}
            >
              {t(`stream.actions.${kind}`)}
            </button>
            {hint && (
              <span id={hintId} className="stream-action-hint" data-testid={hintId}>{hint}</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StreamActionBar;
